import React, { useRef, useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import html2canvas from 'html2canvas';
import useGetPartnerProfile from '../../hooks/useGetPartnerProfile';
import toast from 'react-hot-toast';
import { fetchMarketingCreativesByCategory, transformCreativeItem } from './utils';
import { fetchMarketingCreativeDetail } from './useMarketingCreatives';

import { ArrowBack } from '../../assets/mui';
import MarketingCreativeCard from './components/MarketingCreativeCard';
import MarketingCreativeModal from './components/MarketingCreativeModal';
import SortDropdown from '../../components/SortDropdown';
import Skeleton from '@mui/material/Skeleton';

interface Tag {
  title: string;
  color: string;
}

interface CreativeCard {
  id?: string | number;
  documentId?: string;
  title: string;
  description: string;
  datePosted: string;
  tags: Tag[];
  views: string;
  image?: string;
  sessionDetails?: any;
  contact?: any;
  actions?: string[];
}
const SORT_OPTIONS = [
  { label: 'New to Old', value: 'dateOfPublication:desc' },
  { label: 'Old to New', value: 'dateOfPublication:asc' },
  // { label: 'Most Viewed', value: 'viewCount:desc' },
];

const MarketingCreativeSection: React.FC = () => {
  const { sectionId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Remove the default hook data, we'll fetch with sort
  // const { data: marketingCreative } = useMarketingCreatives();
  const [marketingCreative, setMarketingCreative] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState<string>('dateOfPublication:desc');
  const { data } = useGetPartnerProfile();
  const [selectedCard, setSelectedCard] = useState<CreativeCard | null>(null);
  const [viewCountOverride, setViewCountOverride] = useState<string | null>(null);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [selectedCardIdx, setSelectedCardIdx] = useState<number | null>(null);

  const captureRef = useRef<HTMLDivElement>(null);

  // Get section data from location state or fallback to API data
  const initialSectionData =
    location.state?.sectionData || marketingCreative?.[parseInt(sectionId || '0')];
  const sectionTitle =
    location.state?.sectionTitle || initialSectionData?.sectionTitle || 'Marketing Creatives';
  const sectionCategory = initialSectionData?.category;

  // Fetch data for the current category with sort
  useEffect(() => {
    let ignore = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        if (!sectionCategory) {
          setMarketingCreative(null);
          return;
        }
        const result = await fetchMarketingCreativesByCategory(sectionCategory, sort);
        // Transform each item using transformCreativeItem
        const transformed = (result.data || []).map(transformCreativeItem);
        if (!ignore) setMarketingCreative([{ category: sectionCategory, data: transformed }]);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchData();
    return () => {
      ignore = true;
    };
  }, [sort, sectionCategory]);

  // Get section data from fetched creatives
  const sectionData = marketingCreative?.[0]
    ? {
        sectionTitle: sectionTitle,
        category: sectionCategory,
        list: marketingCreative[0].data.map((item: any) => ({
          ...item,
        })),
      }
    : null;
  // Handle card click to fetch latest view count (robust async pattern)
  const handleCardClick = async (card: CreativeCard, cardIdx: number) => {
    setSelectedCard(card);
    setSelectedCardIdx(cardIdx);
    setViewCountOverride(null);
    setIsModalLoading(true);
    if (card.documentId) {
      fetchMarketingCreativeDetail(card.documentId)
        .then((detail) => {
          if (detail && (detail.viewCount || detail.views)) {
            setViewCountOverride(String(detail.viewCount || detail.views));
          }
        })
        .catch((error) => {
          console.error('Failed to fetch detailed card data:', error);
        })
        .finally(() => {
          setIsModalLoading(false);
        });
    } else {
      setIsModalLoading(false);
    }
  };

  // Handle modal close and update card view count
  const handleCloseModal = () => {
    if (
      selectedCard &&
      viewCountOverride !== null &&
      selectedCardIdx !== null &&
      marketingCreative?.[0]?.data
    ) {
      setMarketingCreative((prev: any) => {
        if (!prev || !prev[0] || !prev[0].data) return prev;
        const updated = [...prev];
        const data = [...updated[0].data];
        data[selectedCardIdx] = {
          ...data[selectedCardIdx],
          views: viewCountOverride,
        };
        updated[0] = { ...updated[0], data };
        return updated;
      });
    }
    setSelectedCard(null);
    setSelectedCardIdx(null);
    setViewCountOverride(null);
    setIsModalLoading(false);
  };

  // Handlers for modal actions (copy, email, WhatsApp, download)
  const handleCopyToClipboard = async () => {
    if (!selectedCard) return;
    try {
      const referralCode = data?.data?.referralCodes?.[0]?.referralCode;
      if (!referralCode) return;
      const referralLink = `https://ultra.go.link/ref?adj_t=1iwdprfa&rc=${referralCode}`;
      const cleanDescription = selectedCard.description
        .replace(/<br\s*\/?>(?!\n)/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .trim();
      const textToCopy = `${selectedCard.title}\n\n${cleanDescription}\n\nJoin here: ${referralLink}`;
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch {}
  };

  const handleEmailShare = () => {
    if (!selectedCard) return;
    const referralCode = data?.data?.referralCodes?.[0]?.referralCode;
    if (!referralCode) return;
    const referralLink = `https://ultra.go.link/ref?adj_t=1iwdprfa&rc=${referralCode}`;
    const cleanDescription = selectedCard.description
      .replace(/<br\s*\/?>(?!\n)/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .trim();
    const subject = encodeURIComponent(selectedCard.title);
    const body = encodeURIComponent(`${cleanDescription}\n\nJoin here: ${referralLink}`);
    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
    window.open(mailtoUrl, '_blank');
  };

  const handleWhatsAppShare = () => {
    if (!selectedCard) return;
    const referralCode = data?.data?.referralCodes?.[0]?.referralCode;
    if (!referralCode) return;
    const referralLink = `https://ultra.go.link/ref?adj_t=1iwdprfa&rc=${referralCode}`;
    const cleanDescription = selectedCard.description
      .replace(/<br\s*\/?>(?!\n)/gi, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .trim();
    const messageText = `${selectedCard.title}\n\n${cleanDescription}\n\nJoin here: ${referralLink}`;
    const message = encodeURIComponent(messageText);
    const whatsappUrl = `https://wa.me/?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleDownload = async () => {
    if (!selectedCard || !captureRef.current) return;

    const convertImageToPng = async (imgSrc: string): Promise<string> => {
      return new Promise((resolve, reject) => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          ctx.drawImage(img, 0, 0);
          try {
            const pngDataUrl = canvas.toDataURL('image/png');
            resolve(pngDataUrl);
          } catch (error) {
            reject(error);
          }
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = imgSrc;
      });
    };

    try {
      // Convert the main image to PNG first
      const mainImg = captureRef.current.querySelector('img');
      if (mainImg && mainImg.src) {
        try {
          const pngDataUrl = await convertImageToPng(mainImg.src);
          // Replace the src temporarily for capture
          const originalSrc = mainImg.src;
          mainImg.src = pngDataUrl;

          // Wait a bit for the image to update
          await new Promise((resolve) => setTimeout(resolve, 100));
          const canvas = await html2canvas(captureRef.current, {
            allowTaint: true,
            useCORS: false,
            scale: 2,
            backgroundColor: '#ffffff',
            logging: false,
            width: 300,
            height: captureRef.current.offsetHeight,
          });

          // Restore original src
          mainImg.src = originalSrc;

          const dataUrl = canvas.toDataURL('image/png', 1.0);
          const link = document.createElement('a');
          link.download = `${selectedCard?.title?.replace(/[^a-z0-9]/gi, '_') || 'creative'}.png`;
          link.href = dataUrl;
          link.click();
          return;
        } catch (conversionError) {
          toast.error(
            'Failed to download image. The image might be from an external source with restrictions.',
          );
          return;
        }
      }

      // Fallback: Direct capture with permissive settings
      const canvas = await html2canvas(captureRef.current, {
        allowTaint: true,
        useCORS: false,
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        width: 300,
        height: captureRef.current.offsetHeight,
      });

      const dataUrl = canvas.toDataURL('image/png', 1.0);
      const link = document.createElement('a');
      link.download = `${selectedCard?.title?.replace(/[^a-z0-9]/gi, '_') || 'creative'}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      toast.error(
        'Failed to download image. The image might be from an external source with restrictions.',
      );
    }
  };

  const handleBack = () => {
    navigate('/marketing-creatives');
  };

  if (loading) {
    return (
      <div className="flex flex-col max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center gap-4 mb-6 mt-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowBack fontSize="small" />
            Back
          </button>
        </div>
        {/* Section skeleton with title and card grid */}
        <div className="space-y-10">
          {[1].map((i) => (
            <div key={i} className="mb-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
                {[1, 2, 3, 4, 5].map((j) => (
                  <Skeleton
                    key={j}
                    variant="rectangular"
                    width={252}
                    height={335}
                    className="rounded-lg"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!sectionData) {
    return (
      <div className="flex flex-col max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center gap-4 mb-6 mt-6">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowBack fontSize="small" />
            Back
          </button>
        </div>
        <div className="text-center py-8">
          <p className="text-gray-600">Section not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col max-w-7xl mx-auto px-4 py-4">
      {/* Header with back button and sort */}
      <div className="flex items-center gap-4 mb-6 mt-6">
        <button
          onClick={handleBack}
          className="bg-green-700/10 flex items-center justify-center p-1.5 h-9 w-9 rounded-full text-green-700"
        >
          <ArrowBack fontSize="small" />
        </button>
        <h1 className="font-medium text-xl text-[#1C1B1F]">{sectionTitle}</h1>
        {/* Sort Dropdown */}
        <div className="ml-auto flex gap-4 items-center">
          <SortDropdown options={SORT_OPTIONS} value={sort} onChange={setSort} />
        </div>
      </div>

      {/* Grid of all items using shared card */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-6">
        {sectionData.list?.map((card: CreativeCard, i: number) => (
          <MarketingCreativeCard
            key={card.id || i}
            card={card}
            onClick={() => handleCardClick(card, i)}
          />
        ))}
      </div>

      {/* Modal using shared modal */}
      {selectedCard && (
        <MarketingCreativeModal
          card={selectedCard}
          isModalLoading={isModalLoading}
          viewCountOverride={viewCountOverride}
          onClose={handleCloseModal}
          onWhatsAppShare={handleWhatsAppShare}
          onEmailShare={handleEmailShare}
          onCopyToClipboard={handleCopyToClipboard}
          onDownload={handleDownload}
          captureRef={captureRef}
          profileData={data?.data?.profile}
          referralCode={data?.data?.referralCodes?.[0]?.referralCode}
        />
      )}
    </div>
  );
};

MarketingCreativeSection.displayName = 'MarketingCreativeSection';
export default MarketingCreativeSection;
